/**
 * Embedded-host metadata helpers — the node-safe half of the embedded-host
 * descriptor, consumed by the session stores, the proxy, auth routes, and
 * tests (no React).
 *
 * The data is now owned by the deep `Copilot` descriptor in
 * `@workspace/copilots` (one source of truth for capabilities + surfaces +
 * per-surface embedded metadata + launch/display). This module is a thin
 * derivation over `embeddedHostMeta` that preserves the helper API the rest of
 * `apps/web` already depends on, so auth/proxy/host-server/routes don't churn.
 *
 * The node-UNSAFE facets live in their own layers, keyed by surface and tied
 * to the descriptor by `__tests__/embedded-hosts.test.ts`:
 *   - shell → `components/embedded-hosts.tsx`
 *   - client-tool adapter → `lib/office/client-tools.ts`
 *   - host-context renderer → `@workspace/ai` `hostContextRenderers`
 */

import { type EmbeddedHostMeta, embeddedHostMeta } from "@workspace/copilots";
import type { EmbeddedSurface } from "@workspace/services/domain";

// Re-export the descriptor-owned names so existing apps/web consumers keep a
// single import site (`@/lib/embedded-hosts`).
export {
  ADDIN_SESSION_COOKIE_NAME,
  type EmbeddedHostMeta,
  embeddedHostMeta,
  PROCORE_SESSION_COOKIE_NAME,
} from "@workspace/copilots";

const bySurface = new Map(embeddedHostMeta.map((m) => [m.surface, m]));

/** Metadata for a surface, or `undefined` if it isn't an embedded host. */
export function getEmbeddedHostMeta(
  surface: string
): EmbeddedHostMeta | undefined {
  return bySurface.get(surface as EmbeddedSurface);
}

/** The generic session-store key for a surface, if it uses the generic store. */
export function sessionStorageKeyForSurface(
  surface: string
): string | undefined {
  return getEmbeddedHostMeta(surface)?.sessionStorageKey;
}

/** Surfaces that capture host context (must each have a server renderer). */
export const contextCapturingSurfaces: readonly EmbeddedSurface[] =
  embeddedHostMeta.filter((m) => m.capturesContext).map((m) => m.surface);

/**
 * Every embedded-surface session cookie name, deduped and sorted for a
 * deterministic check order. `getSession()` and the proxy consult these in
 * addition to the Better-Auth cookie. (Office's surfaces share one name.)
 */
export const embeddedSessionCookieNames: readonly string[] = [
  ...new Set(
    embeddedHostMeta
      .map((m) => m.sessionCookieName)
      .filter((name): name is string => Boolean(name))
  ),
].sort();

/** True for a cross-site iframe copilot that must only ever run framed. */
export function isFramedOnlySurface(surface: string): boolean {
  return getEmbeddedHostMeta(surface)?.framedOnly === true;
}

/**
 * Whether an embedded host marker should be ignored for this request: a
 * framed-only surface loaded as a TOP-LEVEL document is a stale host-cookie
 * leak, not a real copilot session. Browsers report the destination context
 * via `Sec-Fetch-Dest` (`iframe` when framed, `document` when top-level).
 * Conservative: when the header is absent the marker is honored.
 */
export function isStaleFramedOnlyMarker(
  surface: string,
  secFetchDest: string | null
): boolean {
  return isFramedOnlySurface(surface) && secFetchDest === "document";
}
